import React, { useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { blogPosts } from '../data/blogs';
import { Helmet } from 'react-helmet-async';
import { BlogPost } from '../data/types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  Calendar, 
  User, 
  Clock, 
  ArrowLeft, 
  Share2,
  Tag,
  Eye,
  ChevronRight
} from 'lucide-react';
import InFeedAd from '../components/Ads/InFeedAd';
import BlogImage from '../components/BlogImage';
import { getBlogImageUrl } from '../utils/generateBlogImage';

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const post: BlogPost | undefined = blogPosts.find((p) => p.slug === slug);

  // Type guards
  const hasCategory = (post: BlogPost): post is BlogPost & { category: string } => {
    return 'category' in post && typeof post.category === 'string';
  };

  const hasTags = (post: BlogPost): post is BlogPost & { tags: string[] } => {
    return 'tags' in post && Array.isArray(post.tags);
  };

  // Utility functions - memoized
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getReadingTime = (content: string) => {
    const wordsPerMinute = 200;
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  // Related posts - memoized and improved
  const relatedPosts = useMemo(() => {
    if (!post) return [];
    
    return blogPosts
      .filter(p => p.id !== post.id)
      .sort((a, b) => Number(b.id) - Number(a.id))
      .slice(0, 3);
  }, [post]);

  // Enhanced JSON-LD Schema
  const blogPostSchema = useMemo(() => {
    if (!post) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "image": getBlogImageUrl(post.image, post.title, hasCategory(post) ? post.category : undefined),
      "author": { 
        "@type": "Person", 
        "name": post.author 
      },
      "publisher": { 
        "@type": "Organization", 
        "name": "TooliQ",
        "logo": { 
          "@type": "ImageObject", 
          "url": "https://tooliq.ai/logo.png",
          "width": 300,
          "height": 60
        }
      },
      "datePublished": post.publishedAt,
      "dateModified": post.publishedAt,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://tooliq.ai/blog/${post.slug}`
      },
      "url": `https://tooliq.ai/blog/${post.slug}`,
      "articleBody": post.content,
      "wordCount": post.content.split(/\s+/).length,
      "keywords": post.keywords || [],
      "isAccessibleForFree": true,
      "genre": hasCategory(post) ? post.category : "Technology"
    };
  }, [post]);

  const handleShare = async () => {
    if (!post) return;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          console.log('Error sharing:', err);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  // 404 state
  if (!post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50 dark:from-gray-900 dark:to-purple-900/20 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
            <Eye className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Article Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            The article you're looking for doesn't exist or has been moved.
          </p>
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors shadow-md"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  const readingTime = getReadingTime(post.content);
  const wordCount = post.content.split(/\s+/).length;
  const shouldShowSecondAd = wordCount > 1500; // More precise threshold

  const postImageUrl = getBlogImageUrl(
    post.image,
    post.title,
    hasCategory(post) ? post.category : undefined
  );
  const metaImageUrl = postImageUrl.startsWith('data:')
    ? 'https://tooliq.ai/og-images/blog.webp'
    : (postImageUrl.startsWith('http') ? postImageUrl : `https://tooliq.ai${postImageUrl}`);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50 dark:from-gray-900 dark:to-purple-900/20">
      <Helmet>
        <title>{`${post.title} – TooliQ AI Blog | Expert AI Tools Guide 2025`}</title>
        <meta name="description" content={`${post.excerpt} Learn from expert insights on AI tools, automation, and artificial intelligence trends.`} />
        <meta name="keywords" content={post.keywords?.join(', ') || 'AI tools, artificial intelligence, machine learning, automation, ChatGPT, generative AI'} />
        
        {/* Canonical and alternate */}
        <link rel="canonical" href={`https://tooliq.ai/blog/${post.slug}`} />
        
        {/* Open Graph - ENHANCED */}
        <meta property="og:title" content={`${post.title} – TooliQ AI Blog`} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={metaImageUrl} />
        <meta property="og:url" content={`https://tooliq.ai/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="TooliQ" />
        <meta property="article:published_time" content={post.publishedAt} />
        <meta property="article:author" content={post.author} />
        {hasCategory(post) && <meta property="article:section" content={post.category} />}
        {hasTags(post) && post.tags.map((tag, i) => (
          <meta key={i} property="article:tag" content={tag} />
        ))}
        
        {/* Twitter Card - ENHANCED */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@TooliQai" />
        <meta name="twitter:title" content={`${post.title} – TooliQ AI Blog`} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={metaImageUrl} />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="author" content={post.author} />
        
        {/* Structured Data */}
        {blogPostSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(blogPostSchema)
            }}
          />
        )}
      </Helmet>

      {/* Breadcrumb Navigation - ENHANCED */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 max-w-4xl">
          <nav className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
              Home
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
              Blog
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 dark:text-white font-medium line-clamp-1">
              {post.title}
            </span>
          </nav>
        </div>
      </div>

      <article className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header Section - ENHANCED */}
        <header className="mb-12">
          {/* Category Badge */}
          {hasCategory(post) && (
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 text-purple-700 dark:text-purple-300 px-4 py-2 rounded-full font-medium text-sm border border-purple-200 dark:border-purple-700/50">
                <Tag className="w-4 h-4" />
                {post.category}
              </div>
            </div>
          )}

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight text-center">
            {post.title}
          </h1>

          {/* Enhanced Excerpt with SEO context */}
          <div className="max-w-3xl mx-auto mb-8 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/10 dark:to-purple-900/10 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
              {post.excerpt}
            </p>
          </div>

          {/* Meta Information - ENHANCED */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span className="font-medium text-gray-700 dark:text-gray-300">{post.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{readingTime}</span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-8">
            <BlogImage
              src={post.image}
              title={post.title}
              category={hasCategory(post) ? post.category : undefined}
              alt={post.title}
              className="w-full h-64 sm:h-80 lg:h-96 object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
          </div>

          {/* Action Buttons - IMPROVED */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all font-medium hover:shadow-md active:scale-95"
              aria-label="Share this article"
            >
              <Share2 className="w-4 h-4" />
              Share Article
            </button>
          </div>
        </header>

        {/* Content Section - ENHANCED */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 md:p-8 lg:p-10 border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
          {/* Tags */}
          {hasTags(post) && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {post.tags.map((tag: string, index: number) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 text-purple-700 dark:text-purple-300 rounded-full text-sm font-medium border border-purple-200 dark:border-purple-700/50"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Table of Contents hint - for longer articles */}
          {wordCount > 2000 && (
            <div className="mb-8 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-700/50">
              <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">
                📖 This is a comprehensive guide ({readingTime}). Use the headings below to navigate to specific sections.
              </p>
            </div>
          )}

          {/* Markdown Content - ENHANCED STYLING */}
          <div className="prose prose-lg dark:prose-invert max-w-none 
                          prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white
                          prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:pb-3 prose-h2:border-b prose-h2:border-gray-200 dark:prose-h2:border-gray-700
                          prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                          prose-h4:text-xl prose-h4:mt-6 prose-h4:mb-3
                          prose-p:mb-6 prose-p:leading-relaxed prose-p:text-gray-700 dark:prose-p:text-gray-300
                          prose-ul:mb-6 prose-ul:mt-4 prose-ol:mb-6 prose-ol:mt-4
                          prose-li:mb-2 prose-li:text-gray-700 dark:prose-li:text-gray-300
                          prose-a:text-purple-600 dark:prose-a:text-purple-400 prose-a:no-underline
                          hover:prose-a:underline prose-a:font-medium prose-a:transition-colors
                          prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-semibold
                          prose-blockquote:border-l-4 prose-blockquote:border-l-purple-600 
                          prose-blockquote:bg-purple-50 dark:prose-blockquote:bg-purple-900/20 
                          prose-blockquote:px-6 prose-blockquote:py-4 prose-blockquote:rounded-r-lg
                          prose-blockquote:my-6 prose-blockquote:not-italic
                          prose-img:rounded-xl prose-img:shadow-lg prose-img:my-8
                          prose-code:bg-gray-100 dark:prose-code:bg-gray-700 
                          prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm
                          prose-code:before:content-[''] prose-code:after:content-['']
                          prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-xl
                          prose-pre:p-6 prose-pre:my-6 prose-pre:overflow-x-auto
                          prose-table:border-collapse prose-table:w-full prose-table:my-8
                          prose-table:border prose-table:border-gray-300 dark:prose-table:border-gray-600
                          prose-th:bg-gray-100 dark:prose-th:bg-gray-700 prose-th:font-semibold
                          prose-th:px-4 prose-th:py-3 prose-th:text-left prose-th:border prose-th:border-gray-300
                          prose-td:px-4 prose-td:py-3 prose-td:border prose-td:border-gray-300
                          dark:prose-td:border-gray-600">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {post.content}
            </ReactMarkdown>
          </div>

          {/* SINGLE STRATEGIC AD - Only for longer articles */}
          {shouldShowSecondAd && (
            <div className="my-12">
              <div className="text-center text-xs text-gray-500 dark:text-gray-400 mb-2">Advertisement</div>
              <InFeedAd />
            </div>
          )}
        </div>

        {/* Related Posts Section - ENHANCED */}
        {relatedPosts.length > 0 && (
          <section className="mt-16" aria-labelledby="related-articles">
            <h2 id="related-articles" className="text-2xl font-bold text-gray-900 dark:text-white mb-8 flex items-center gap-2">
              <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              Continue Reading: Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl p-5 shadow-sm border border-gray-200/50 dark:border-gray-700/50 hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
                >
                  <div className="h-40 rounded-lg overflow-hidden mb-4">
                    <BlogImage
                      src={relatedPost.image}
                      title={relatedPost.title}
                      category={hasCategory(relatedPost) ? relatedPost.category : undefined}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-2 line-clamp-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                    {relatedPost.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mb-3">
                    {relatedPost.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                    <time dateTime={relatedPost.publishedAt}>{formatDate(relatedPost.publishedAt)}</time>
                    <span>•</span>
                    <span>{getReadingTime(relatedPost.content)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Footer CTA - ENHANCED */}
        <footer className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-700">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-white shadow-2xl text-center">
            <h3 className="text-2xl font-bold mb-4">
              Found This Article Valuable?
            </h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto text-lg">
              Share it with your network and help others discover AI insights. Follow us for daily updates 
              on the latest AI tools, tutorials, and industry trends.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={handleShare}
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-purple-600 rounded-lg hover:bg-gray-100 font-bold shadow-md hover:shadow-lg transition-all active:scale-95"
              >
                <Share2 className="w-5 h-5" />
                Share This Article
              </button>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white/10 font-bold rounded-lg transition-all"
              >
                <ArrowLeft className="w-5 h-5" />
                Browse More Articles
              </Link>
            </div>
          </div>

          {/* Author Attribution */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Written by <strong className="text-gray-900 dark:text-white">{post.author}</strong>
              {' • '}
              Published on <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
              {hasCategory(post) && (
                <>
                  {' • '}
                  Category: <strong className="text-purple-600 dark:text-purple-400">{post.category}</strong>
                </>
              )}
            </p>
          </div>
        </footer>
      </article>
    </div>
  );
};

export default BlogDetail;